import * as stylex from "@stylexjs/stylex";
import { sectionTarget } from "@/lib/last-place";
import { useRememberedState, viewStateKey } from "@/lib/remembered-view-state";
const NO_ASSIGNMENT_FILTERS: Record<string, string[]> = {};
import { mediaPerformancePath } from "@/lib/reporting-view";
import { createReportSearchParams } from "@/lib/report-search-params";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { keepPreviousData, type QueryClient, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate, useParams, useRouterState } from "@tanstack/react-router";
import type { inferRouterOutputs } from "@trpc/server";
import { parseCreativeThumbnailCacheSnapshot, readCreativeThumbnailCacheSnapshot, saveCreativeThumbnailCache } from "@/components/analytics/creative-thumbnail-cache";
import { chunkPreviewAds, isRenderablePreviewUrl } from "@/lib/creative-preview";
import EntityBreakdownWithExtras from "@/components/analytics/EntityBreakdownWithExtras";
import { CreativeLayoutControl, type CreativeLayout } from "@/components/analytics/EntityBreakdown";
import CreativeDetail from "@/components/analytics/CreativeDetail";
import AnalyticsExportHeader from "@/components/analytics/AnalyticsExportHeader";
import WorkspaceDefaultActions from "@/components/analytics/WorkspaceDefaultActions";
import { captureAnalyticsPng, captureAnalyticsCsv } from "@/components/analytics/analytics-export";
import DataGrid from "@/components/DataGrid";
import ReportingToolbar from "@/components/ReportingToolbar";
import type { ChangeDisplay } from "@/components/ReportingToolbar";
import PageHeader from "@/components/shell/PageHeader";
import Button from "@/components/ui/Button";
import Chip from "@/components/ui/Chip";
import ConfirmDialog from "@/components/ui/ConfirmDialog";
import EmptyState from "@/components/ui/EmptyState";
import { Page, PageBody } from "@/components/ui/Page";
import { TableSkeleton, UpdatingChip } from "@/components/ui/Skeleton";
import { TabList, tabProps } from "@/components/ui/Tabs";
import "@/design/tokens.stylex";
import { colors, space, text } from "@/design/tokens.stylex";
import { useWindowTitleDetail } from "@/lib/window-title";
import { Route as OrgRoute } from "./$orgSlug";
import { trpc, trpcClient } from "@/lib/trpc";
import type { ManualDataCell, MeasurementState } from "@aco/core";
import { autoGrouping } from "@aco/core";
import type { Platform } from "@aco/core";
import { getDatePreset } from "@/lib/utils";
import { dateChangePatch, filterChangePatch, type DateChange, type FilterChange } from "@/lib/reporting-apply";
import { useSearchPatch } from "@/lib/search-nav";
import { creativeEntityKey } from "@/lib/creative-entity";
import type { CreativeStatusAction } from "@/lib/creative-status-action";
import type { BreakdownRow } from "@/lib/breakdown-sort";
import type { DeltaDisplay, OrgSearch } from "@/lib/search-params";
import type { AnalyticsView } from "@/lib/search-params";
import { effectiveComparisonMode, presetComparisonMode } from "@/lib/search-params";
import { getComparisonPeriod } from "@/lib/dateUtils";
import { showError, showSuccess } from "@/lib/toast";
import { MANUAL_VIEW_REFRESH_MAX_DAYS, planManualViewRefresh } from "@/lib/manual-view-refresh";
import type { AppRouter } from "@/server/api/root";
type GridDataOutput = inferRouterOutputs<AppRouter>["gridData"]["get"];
type GridDataDeps = Pick<OrgSearch, "start" | "end" | "preset" | "platform" | "grouping" | "comparisonMode" | "deltas" | "segment" | "breakdown">;

// Recompute a Manual data row's range total from its cells after an optimistic
// edit. Mirrors the server builder (buildManualDataGroup): a bucket counts as
// measured only when its state is `measured` or `zero`; a partially filled
// range is `incomplete`, an empty one `unmeasured`.
function recomputeManualTotal(cells: Record<string, ManualDataCell>, bucketKeys: string[]): {
  value: number | null;
  state: MeasurementState;
} {
  let measured = 0;
  let sum = 0;
  for (const key of bucketKeys) {
    const cell = cells[key];
    if (cell && (cell.state === "measured" || cell.state === "zero")) {
      measured += 1;
      sum += cell.value ?? 0;
    }
  }
  if (measured === 0) return {
    value: null,
    state: "unmeasured"
  };
  if (measured < bucketKeys.length) return {
    value: null,
    state: "incomplete"
  };
  return {
    value: sum,
    state: sum === 0 ? "zero" : "measured"
  };
}

// Patch the grid cache for one optimistic Manual data edit. `next` is the new
// value, or null for a delete (blank cell). Inline editing runs only at day
// grouping, so `day` is exactly one bucket key.
function applyManualEdit(data: GridDataOutput, metricId: string, day: string, next: number | null): GridDataOutput {
  if (!data.manualData) return data;
  const bucketKeys = data.buckets.map(bucket => bucket.key);
  const rows = data.manualData.rows.map(row => {
    if (row.metricId !== metricId) return row;
    const existing = row.cells[day];
    if (!existing) return row;
    const cell: ManualDataCell = next == null ? {
      ...existing,
      value: null,
      state: "unmeasured"
    } : {
      ...existing,
      value: next,
      state: next === 0 ? "zero" : "measured"
    };
    // Week/month comparisons can overlap the edited range. Until the server
    // rebuilds both sides, suppress deltas instead of retaining a stale baseline.
    const cells = Object.fromEntries(Object.entries({
      ...row.cells,
      [day]: cell
    }).map(([key, value]) => [key, {
      ...value,
      comparisonValue: null
    }]));
    return {
      ...row,
      cells,
      total: recomputeManualTotal(cells, bucketKeys)
    };
  });
  return {
    ...data,
    manualData: {
      rows
    }
  };
}
function campaignFilterKey(row: {
  platform: Platform;
  accountId: string;
  campaignId: string | null;
  name: string;
}) {
  return `${row.platform}:${row.accountId}:${row.campaignId ?? row.name}`;
}

// Reporting is completed-day data. Keep identical views warm locally; the
// sync-status listener below invalidates this immediately when fresh rows land.
const GRID_STALE_TIME = 30 * 60_000;
const GRID_GC_TIME = 60 * 60_000;

// Maps `loaderDeps` (typed search params) to the exact `gridData.get` input —
// shared by the loader's `ensureQueryData` and the component's `useQuery` so
// both hit the same cache entry.
function gridDataInput(orgSlug: string, deps: GridDataDeps, includeCreativeAnalysis = false) {
  return {
    orgSlug,
    start: deps.start || getDatePreset("last7").start,
    end: deps.end || getDatePreset("last7").end,
    grouping: deps.grouping,
    platform: deps.platform,
    // The baseline follows the preset (see presetComparisonMode). Keep
    // accepting legacy comparisonMode params, but do not let an old link
    // silently change what the simplified Number / % control compares against.
    comparisonMode: effectiveComparisonMode(deps.deltas, presetComparisonMode(deps.preset)),
    segmentId: deps.segment === "all" ? undefined : deps.segment,
    breakdownIds: deps.breakdown,
    includeCreativeAnalysis
  };
}
export const Route = createFileRoute("/_authenticated/$orgSlug/")({
  // The parent route (`$orgSlug`) already validated these fields via
  // `orgSearchSchema` — consume that typed search directly instead of
  // re-casting each field.
  loaderDeps: ({
    search
  }) => ({
    start: search.start,
    end: search.end,
    preset: search.preset,
    platform: search.platform,
    grouping: search.grouping,
    comparisonMode: search.comparisonMode,
    deltas: search.deltas,
    segment: search.segment,
    breakdown: search.breakdown
  }),
  beforeLoad: ({
    params,
    search,
    location
  }) => {
    const next = new URLSearchParams(location.searchStr);
    // A bare `/<org>` lands on its remembered section (Last place). Any
    // search param is an explicit Media Performance request and wins.
    if (next.size === 0) {
      throw redirect({
        ...sectionTarget(params.orgSlug),
        replace: true
      });
    }
    const query = `?${next.toString()}`;
    throw redirect({
      href: `${mediaPerformancePath(params.orgSlug, search.view ?? "overview")}${query}`,
      replace: true
    });
  }
});

// The loader warms the cache but does not block the route on it (issue 32):
// the page renders immediately and owns its own loading feedback — a table
// skeleton on a cold key, the previous rows plus an "Updating…" chip when a
// filter changes on already-loaded data. Awaiting here would hand every slow
// refetch to the route pending state and blank the grid instead.
export function mediaPerformanceLoader(orgSlug: string, deps: GridDataDeps, queryClient: QueryClient, includeCreativeAnalysis = false) {
  void queryClient.ensureQueryData({
    ...trpc.gridData.get.queryOptions(gridDataInput(orgSlug, deps, includeCreativeAnalysis)),
    staleTime: GRID_STALE_TIME,
    gcTime: GRID_GC_TIME
  }).catch(() => undefined);
  void queryClient.ensureQueryData(trpc.segments.list.queryOptions({
    orgSlug
  })).catch(() => undefined);
}

// Which section a media-performance pathname addresses. Used to reflect the
// pending navigation's target tab before its route has committed.
export function viewFromPathname(pathname: string): AnalyticsView | null {
  const match = /\/media-performance(?:\/([^/?#]+))?\/?$/.exec(pathname);
  if (!match) return null;
  switch (match[1]) {
    case undefined:
    case "":
      return "overview";
    case "campaigns":
      return "campaign";
    case "creatives":
      return "creative";
    default:
      return null;
  }
}

// Route-level pending state (code-split chunk still loading): the real page
// header with the target section's tab already highlighted, over the shared
// table skeleton — never an empty white card.
export function MediaPerformancePending() {
  const pendingView = useRouterState({
    select: state => viewFromPathname(state.location.pathname)
  });
  return <Page>
      <PageHeader title="Media Performance" tabs={<AnalyticsTabs view={pendingView ?? "overview"} onChange={() => undefined} />} />
      <PageBody>
        <MediaPerformanceBodySkeleton view={pendingView ?? "overview"} />
      </PageBody>
    </Page>;
}

// Section-shaped skeleton for the grid area while a cold gridData key loads.
function MediaPerformanceBodySkeleton({
  view
}: {
  view: AnalyticsView;
}) {
  const label = view === "campaign" ? "Loading campaigns" : view === "creative" ? "Loading creatives" : "Loading reporting data";
  return <TableSkeleton label={label} toolbar rows={view === "overview" ? 10 : 8} columns={view === "overview" ? 7 : 5} />;
}
export function MediaPerformancePage({
  viewOverride
}: {
  viewOverride?: AnalyticsView;
} = {}) {
  const params = useParams({
    from: "/_authenticated/$orgSlug"
  });
  const search = OrgRoute.useSearch();
  const view: AnalyticsView = viewOverride ?? search.view ?? "overview";
  const activeGridInput = useMemo(() => gridDataInput(params.orgSlug, {
    start: search.start,
    end: search.end,
    preset: search.preset,
    platform: search.platform,
    grouping: search.grouping,
    comparisonMode: search.comparisonMode,
    deltas: search.deltas,
    segment: search.segment,
    breakdown: search.breakdown
  }, view === "creative"), [params.orgSlug, search.start, search.end, search.preset, search.platform, search.grouping, search.comparisonMode, search.deltas, search.segment, search.breakdown, view]);
  const gridQuery = useQuery({
    ...trpc.gridData.get.queryOptions(activeGridInput),
    staleTime: GRID_STALE_TIME,
    gcTime: GRID_GC_TIME,
    // A filter change (date range, platform, segment, comparison) keeps the
    // previous rows on screen with the updating chip instead of blanking the
    // grid. A section-family change (overview/campaign ↔ creative)
    // asks for a different result shape, so it starts from the skeleton.
    placeholderData: (previous, previousQuery) => {
      const previousInput = previousQuery?.queryKey?.[1] as {
        input?: {
          includeCreativeAnalysis?: boolean;
        };
      } | undefined;
      return previousInput?.input?.includeCreativeAnalysis === activeGridInput.includeCreativeAnalysis ? previous : undefined;
    }
  });
  const segmentsQuery = useQuery(trpc.segments.list.queryOptions({
    orgSlug: params.orgSlug
  }));
  const navigate = useNavigate();
  const navigateDashboard = useCallback((nextView: AnalyticsView) => {
    const next = createReportSearchParams(search, new Set(["view", "campaign", "creative"]));
    const query = next.size > 0 ? `?${next.toString()}` : "";
    void navigate({
      href: `${mediaPerformancePath(params.orgSlug, nextView)}${query}`
    });
  }, [navigate, params.orgSlug, search]);
  if (!gridQuery.data) {
    return <Page>
        <PageHeader title="Media Performance" actions={<WorkspaceDefaultActions />} tabs={<AnalyticsTabs view={view} onChange={next => navigateDashboard(next)} />} />
        <PageBody>
          {gridQuery.isError ? <div role="alert" className="x78zum5 xdt5ytf x1cy8zhl x166cprz x1e3avs9 xtu3avg">
              <p className="x1ghz6dp">
                Reporting data could not be loaded: {gridQuery.error.message}
              </p>
              <Button variant="secondary" size="sm" onClick={() => void gridQuery.refetch()}>
                Try again
              </Button>
            </div> : <MediaPerformanceBodySkeleton view={view} />}
        </PageBody>
      </Page>;
  }
  return <MediaPerformanceLoaded view={view} data={gridQuery.data} updating={gridQuery.isPlaceholderData || gridQuery.isFetching && !gridQuery.isLoading} segments={segmentsQuery.data ?? []} activeGridInput={activeGridInput} navigateDashboard={navigateDashboard} />;
}
function MediaPerformanceLoaded({
  view,
  data,
  updating,
  segments,
  activeGridInput,
  navigateDashboard
}: {
  view: AnalyticsView;
  data: GridDataOutput;
  updating: boolean;
  segments: {
    id: string;
    name: string;
  }[];
  activeGridInput: ReturnType<typeof gridDataInput>;
  navigateDashboard: (nextView: AnalyticsView) => void;
}) {
  const params = useParams({
    from: "/_authenticated/$orgSlug"
  });
  const search = OrgRoute.useSearch();
  const selectedSegment = useMemo(() => search.segment ? segments.find(segment => segment.id === search.segment) : undefined, [search.segment, segments]);
  const {
    currentOrg
  } = OrgRoute.useLoaderData();
  const updateSearch = useSearchPatch<OrgSearch>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingPng, setIsExportingPng] = useState(false);
  const [confirmingWideRefresh, setConfirmingWideRefresh] = useState(false);
  const [manualRefreshRunActive, setManualRefreshRunActive] = useState(false);
  const [manualSaveError, setManualSaveError] = useState<string | null>(null);
  const [creativeLayout, setCreativeLayout] = useRememberedState<CreativeLayout>(viewStateKey(params.orgSlug, "creative", "layout"), "list");
  // Assignment filters live beside the Breakdown selector and are remembered
  // per view, so By campaign and By creative each keep their own.
  const assignmentView = view === "campaign" ? "campaign" : "creative";
  const [assignmentFilters, setAssignmentFilters] = useRememberedState<Record<string, string[]>>(viewStateKey(params.orgSlug, assignmentView, "assignments"), NO_ASSIGNMENT_FILTERS);
  // Stored thumbnail paths are durable and same-origin. Reuse the last-known ad-id mapping
  // immediately on reload instead of holding every image behind the thumbnail
  // metadata request. React Query still reconciles new/changed ads below.
  const creativeThumbnailSnapshot = useSyncExternalStore(useCallback(() => () => undefined, []), useCallback(() => readCreativeThumbnailCacheSnapshot(params.orgSlug), [params.orgSlug]), () => "");
  const creativeThumbnailCache = useMemo(() => parseCreativeThumbnailCacheSnapshot(creativeThumbnailSnapshot), [creativeThumbnailSnapshot]);
  const [requestedThumbnailStorage] = useState(() => new Set<string>());
  const gridRef = useRef<HTMLDivElement>(null);
  const lastSyncSuccessRef = useRef<string | null | undefined>(undefined);
  const handledCompletedRefreshRef = useRef<string | null>(null);
  const refreshCurrentViewMutation = useMutation(trpc.gridData.refreshCurrentView.mutationOptions());
  const advanceReportingRefreshMutation = useMutation(trpc.gridData.advanceReportingRefresh.mutationOptions());
  const advanceReportingRefresh = advanceReportingRefreshMutation.mutateAsync;
  const advancingReportingRefresh = advanceReportingRefreshMutation.isPending;

  // Durable background refresh is enqueued and drained server-side (issue 07),
  // so the tab does not need to poll to make progress. We watch the workspace's
  // reporting status ONLY to know when to refresh the grid — and we stop polling
  // the instant no job is unfinished. A settled workspace makes zero repeat
  // status calls; a fixed open-tab interval is gone.
  const hasReportingAccounts = currentOrg.metaAccountIds.length > 0 || currentOrg.googleAccountIds.length > 0 || currentOrg.tiktokAccountIds.length > 0 || (currentOrg.snapchatAccountIds?.length ?? 0) > 0;
  const reportingSyncStatusOptions = trpc.gridData.reportingSyncStatus.queryOptions({
    orgSlug: params.orgSlug,
    refreshToken: data.refreshQueuedAt
  });
  const {
    data: reportingSyncStatus
  } = useQuery({
    ...reportingSyncStatusOptions,
    enabled: hasReportingAccounts,
    // Poll only while a job is still being observed as unfinished; once the
    // queue is empty (`active === 0`) refetchInterval returns false and polling
    // halts until the next observed job or an explicit refetch.
    refetchInterval: query => (query.state.data?.active ?? 0) > 0 ? 5_000 : false,
    refetchIntervalInBackground: false
  });
  useEffect(() => {
    const latest = reportingSyncStatus?.lastSuccessAt ?? null;
    if (lastSyncSuccessRef.current !== undefined && latest !== lastSyncSuccessRef.current) {
      void queryClient.invalidateQueries({
        queryKey: trpc.gridData.get.queryKey(activeGridInput)
      });
    }
    lastSyncSuccessRef.current = latest;
  }, [reportingSyncStatus?.lastSuccessAt, queryClient, activeGridInput]);
  useEffect(() => {
    const token = data.refreshQueuedAt;
    if (!token || reportingSyncStatus === undefined || reportingSyncStatus.active > 0 || handledCompletedRefreshRef.current === token) {
      return;
    }
    // The worker may finish before the first token-scoped status read. There is
    // then no active→settled transition for lastSuccessAt to observe, so perform
    // the one necessary invalidation here.
    handledCompletedRefreshRef.current = token;
    void queryClient.invalidateQueries({
      queryKey: trpc.gridData.get.queryKey(activeGridInput)
    });
  }, [data.refreshQueuedAt, reportingSyncStatus, queryClient, activeGridInput]);
  const reportingJobsActive = (reportingSyncStatus?.active ?? 0) > 0;
  useEffect(() => {
    if (!reportingJobsActive) {
      setManualRefreshRunActive(false);
      return;
    }
    if (currentOrg.role === "viewer" || advancingReportingRefresh) return;
    // One invocation is bounded. Resume observed work after a reload too;
    // otherwise a queued job can keep polling without any worker advancing it.
    // Closing the tab is
    // safe: the daily recovery sweep remains the durable backstop.
    const timer = window.setTimeout(() => {
      void advanceReportingRefresh({
        orgSlug: params.orgSlug
      }).then(() => queryClient.invalidateQueries({
        queryKey: trpc.gridData.reportingSyncStatus.pathKey()
      })).catch(() => undefined);
    }, 30_000);
    return () => window.clearTimeout(timer);
  }, [advanceReportingRefresh, advancingReportingRefresh, currentOrg.role, params.orgSlug, queryClient, reportingJobsActive]);
  const preset = search.preset || "last7";
  const grouping = search.grouping || autoGrouping(data.dateRange.start, data.dateRange.end);
  const platform = (search.platform || "all") as "all" | Platform;
  const manualRefreshPlan = useMemo(() => planManualViewRefresh({
    start: activeGridInput.start,
    end: activeGridInput.end,
    comparisonMode: activeGridInput.comparisonMode
  }), [activeGridInput.comparisonMode, activeGridInput.end, activeGridInput.start]);
  // Deltas default OFF (calm first read); an explicit choice persists in the
  // URL so shared links and reloads keep it.
  const deltaDisplay: DeltaDisplay = search.deltas || "hidden";
  const navigateCampaignCreatives = useCallback((row: GridDataOutput["campaignBreakdown"][number]) => {
    const next = createReportSearchParams(search, new Set(["view", "campaign", "creative"]));
    next.set("campaign", campaignFilterKey(row));
    void navigate({
      href: `/${params.orgSlug}/media-performance/creatives?${next.toString()}`
    });
  }, [navigate, params.orgSlug, search]);
  // The scope the extra metric columns are read for: the report's own period,
  // platforms and Filter.
  const extraScope = useMemo(() => ({
    start: data.dateRange.start,
    end: data.dateRange.end,
    platforms: data.availablePlatforms.filter(item => platform === "all" || item === platform),
    segmentId: search.segment === "all" ? undefined : search.segment
  }), [data.availablePlatforms, data.dateRange.end, data.dateRange.start, platform, search.segment]);
  const selectedCampaign = useMemo(() => search.campaign ? data.campaignBreakdown.find(row => campaignFilterKey(row) === search.campaign) : undefined, [data.campaignBreakdown, search.campaign]);
  const campaignCreativeBreakdown = useMemo(() => search.campaign ? data.creativeBreakdown.filter(row => campaignFilterKey(row) === search.campaign) : data.creativeBreakdown, [data.creativeBreakdown, search.campaign]);
  // Every eligible creative is enrichment-eligible, not just the first 150 rows:
  // the eligible set is split into bounded batches so a large, sorted, or
  // filtered creative list still gets previews without an oversized request
  // (issue 14). Enrichment stays off the initial render path — the grid renders
  // from `data` and these queries fill previews in afterwards.
  const creativeAdBatches = useMemo(() => chunkPreviewAds(campaignCreativeBreakdown.filter(row => (row.platform === "meta" || row.platform === "tiktok" || row.platform === "snapchat") && row.adId).map(row => ({
    adId: row.adId as string,
    platform: row.platform as "meta" | "tiktok" | "snapchat"
  }))), [campaignCreativeBreakdown]);
  const previewsEnabled = view === "creative";
  const creativeThumbnailQueries = useQueries({
    queries: creativeAdBatches.map(ads => ({
      ...trpc.gridData.creativeThumbnails.queryOptions({
        orgSlug: params.orgSlug,
        ads
      }),
      enabled: previewsEnabled && ads.length > 0,
      staleTime: 6 * 60 * 60_000,
      gcTime: 12 * 60 * 60_000,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
      retry: 1
    }))
  });
  const creativeThumbnails = useMemo(() => creativeThumbnailQueries.flatMap(query => query.data ?? []), [creativeThumbnailQueries]);
  useEffect(() => {
    const ads = creativeThumbnails.flatMap(item => {
      const key = `${item.platform}:${item.adId}`;
      if (!item.needsStorage || requestedThumbnailStorage.has(key)) return [];
      requestedThumbnailStorage.add(key);
      return [{
        adId: item.adId,
        platform: item.platform
      }];
    });
    if (ads.length === 0) return;
    const input = {
      orgSlug: params.orgSlug,
      ads
    };
    void trpcClient.gridData.cacheCreativeThumbnails.mutate(input).then(result => {
      if (result.stored === 0) return;
      // Re-read every preview batch so freshly stored durable URLs replace the
      // provider source across the whole eligible set.
      return queryClient.invalidateQueries({
        queryKey: trpc.gridData.creativeThumbnails.pathKey()
      });
    }).catch(() => {
      for (const ad of ads) requestedThumbnailStorage.delete(`${ad.platform}:${ad.adId}`);
    });
  }, [creativeThumbnails, params.orgSlug, queryClient, requestedThumbnailStorage]);
  const stableCreativeThumbnails = useMemo(() => {
    const thumbnails = new Map(creativeThumbnailCache);
    for (const item of creativeThumbnails) {
      if (item.thumbnailUrl) {
        thumbnails.set(`${item.platform}:${item.adId}`, item.thumbnailUrl);
      }
    }
    return thumbnails;
  }, [creativeThumbnailCache, creativeThumbnails]);
  const stableCreativeMedia = useMemo(() => {
    const media = new Map<string, string>();
    for (const item of creativeThumbnails) {
      if (item.mediaUrl) media.set(`${item.platform}:${item.adId}`, item.mediaUrl);
    }
    return media;
  }, [creativeThumbnails]);
  useEffect(() => {
    saveCreativeThumbnailCache(params.orgSlug, stableCreativeThumbnails);
  }, [params.orgSlug, stableCreativeThumbnails]);
  const creativeRows = useMemo(() => {
    return campaignCreativeBreakdown.map(row => {
      const key = `${row.platform}:${row.adId}`;
      // The table poster uses a compact recognition-quality derivative of the
      // stored preview rather than an original publishing-quality asset.
      return {
        ...row,
        thumbnailUrl: row.adId ? renderableOrNull(stableCreativeThumbnails.get(key)) : null,
        previewMediaUrl: row.adId ? stableCreativeMedia.get(key) ?? null : null
      };
    });
  }, [stableCreativeThumbnails, stableCreativeMedia, campaignCreativeBreakdown]);

  // The By creative detail is a same-page state addressed by the selected
  // creative's stable provider/account/entity identity in the URL, so the
  // selection is shareable and restored by browser navigation.
  const selectedCreative = useMemo(() => search.creative ? creativeRows.find(row => creativeEntityKey(row) === search.creative) : undefined, [creativeRows, search.creative]);
  // An open creative names the window: Company · Media Performance · <creative>.
  useWindowTitleDetail(view === "creative" ? selectedCreative?.name : null);
  const navigateCreativeDetail = useCallback((row: BreakdownRow) => {
    updateSearch({
      creative: creativeEntityKey(row)
    });
  }, [updateSearch]);
  // Live Delivery/Ad status (issue 11 contract) for the open creative. Only
  // Meta and TikTok ads expose a live status path today; Google stays deferred.
  const statusEntity = view === "creative" && selectedCreative && (selectedCreative.platform === "meta" || selectedCreative.platform === "tiktok") && selectedCreative.adId ? {
    platform: selectedCreative.platform,
    accountId: selectedCreative.accountId,
    entityId: selectedCreative.adId
  } : null;
  const {
    data: entityStatuses,
    isLoading: creativeStatusLoading
  } = useQuery({
    ...trpc.connections.entityStatuses.queryOptions({
      slug: params.orgSlug,
      entities: statusEntity ? [statusEntity] : []
    }),
    enabled: statusEntity != null,
    staleTime: 60_000,
    refetchOnWindowFocus: false
  });
  const creativeStatusResult = entityStatuses?.statuses?.[0];
  const creativeDetailStatus = creativeStatusResult ? {
    delivery: creativeStatusResult.delivery,
    ad: creativeStatusResult.ad,
    refreshedAt: creativeStatusResult.refreshedAt
  } : null;

  // Safe Pause/Resume (issue 15): the server re-reads the provider, guards
  // against status/parent drift since the member saw the detail, and commits
  // through the durable Edit run. We only report the decision and refresh the
  // displayed status; the ad is never mutated optimistically.
  const [statusActionPending, setStatusActionPending] = useState(false);
  const handleCreativeStatusAction = useCallback(async (action: CreativeStatusAction) => {
    if (!statusEntity) return;
    setStatusActionPending(true);
    try {
      const result = await trpcClient.launchEdit.creativeStatusAction.mutate({
        orgSlug: params.orgSlug,
        platform: statusEntity.platform,
        accountId: statusEntity.accountId,
        adId: statusEntity.entityId,
        action,
        expected: {
          ad: creativeDetailStatus?.ad ?? null,
          delivery: creativeDetailStatus?.delivery ?? null
        }
      });
      const pastTense = action === "pause" ? "paused" : "resumed";
      if (result.decision === "committed") {
        if (result.runOutcome === "committed") {
          showSuccess(`Ad ${pastTense}`);
        } else if (result.runOutcome === "verification_required") {
          showSuccess("Submitted — the provider is still confirming the change.");
        } else if (result.runOutcome === "conflict") {
          showError("The ad changed on the provider first, so nothing was changed.");
        } else if (result.runOutcome === "rejected") {
          showError("The provider refused this change.");
        } else {
          showError("The change could not be completed. Nothing was changed.");
        }
      } else if (result.decision === "stale_ad") {
        showError(`This ad's status changed since you opened it, so it was not ${pastTense}.`);
      } else if (result.decision === "stale_delivery") {
        showError("This ad's delivery changed since you opened it, so nothing was changed.");
      } else if (result.decision === "blocked") {
        showError(result.reasons[0] ?? "This ad cannot be changed right now.");
      } else {
        showError("This ad's status can't be changed from here.");
      }
    } catch (error) {
      showError(error instanceof Error ? error.message : "The change could not be completed.");
    } finally {
      setStatusActionPending(false);
      await queryClient.invalidateQueries({
        queryKey: trpc.connections.entityStatuses.queryKey()
      });
    }
  }, [statusEntity, creativeDetailStatus, params.orgSlug, queryClient]);

  // The Date and Filter panels stage their edits; Apply lands them in one navigation.
  const handleDateApply = useCallback((change: DateChange) => updateSearch(dateChangePatch(change)), [updateSearch]);
  const handleFilterApply = useCallback((change: FilterChange) => {
    const patch = filterChangePatch(change);
    if (Object.keys(patch).length) updateSearch(patch);
    if (change.assignments) setAssignmentFilters(change.assignments);
  }, [setAssignmentFilters, updateSearch]);
  const changeDisplay: ChangeDisplay = deltaDisplay === "hidden" ? "off" : deltaDisplay;
  const exportComparison = getComparisonPeriod(data.dateRange.start, data.dateRange.end, activeGridInput.comparisonMode);
  const renderExportImage = useCallback(async () => {
    const node = gridRef.current;
    if (!node) throw new Error("The current report is not ready to export.");
    return captureAnalyticsPng(node);
  }, []);
  const handleDownloadPng = useCallback(async () => {
    setIsExportingPng(true);
    try {
      const blob = await renderExportImage();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${currentOrg.slug}-media-performance-${view}-${data.dateRange.start}-to-${data.dateRange.end}.png`;
      anchor.click();
      URL.revokeObjectURL(url);
      showSuccess("PNG downloaded");
    } catch (error) {
      showError(error instanceof Error ? error.message : "Could not export the analytics image.");
      throw error;
    } finally {
      setIsExportingPng(false);
    }
  }, [currentOrg.slug, data.dateRange.end, data.dateRange.start, renderExportImage, view]);
  const handleCopyPng = useCallback(async () => {
    setIsExportingPng(true);
    try {
      if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined") {
        throw new Error("Image copying is not supported in this browser. Download the PNG instead.");
      }
      const blobPromise = renderExportImage();
      await navigator.clipboard.write([new ClipboardItem({
        "image/png": blobPromise
      })]);
      showSuccess("Image copied — paste it directly into Slack");
    } catch (error) {
      showError(error instanceof Error ? error.message : "Could not export the analytics image.");
      throw error;
    } finally {
      setIsExportingPng(false);
    }
  }, [renderExportImage]);

  // Manual data inline editing (issue 16). Only admins and members reach these
  // handlers; a Viewer's cells are read-only (and the write is rejected at the
  // capability boundary regardless). Saves are optimistic and quiet: the grid
  // cache is patched immediately, the mutation runs, and on failure the cache
  // is rolled back and an accessible error is announced.
  const canEditManualData = currentOrg.role !== "viewer";
  const manualQueryKey = useMemo(() => trpc.gridData.get.queryKey(activeGridInput), [activeGridInput]);
  const patchManualCell = useCallback((metricId: string, day: string, next: number | null) => {
    const previous = queryClient.getQueryData<GridDataOutput>(manualQueryKey);
    queryClient.setQueryData<GridDataOutput>(manualQueryKey, current => current ? applyManualEdit(current, metricId, day, next) : current);
    return previous;
  }, [manualQueryKey, queryClient]);
  const handleManualSave = useCallback(async (metricId: string, day: string, value: number) => {
    setManualSaveError(null);
    const previous = patchManualCell(metricId, day, value);
    try {
      await trpcClient.manualData.setValue.mutate({
        orgId: currentOrg.id,
        metricId: Number(metricId),
        day,
        value
      });
      // Quiet success: advance the authoritative cache in the background so
      // the row total reflects the server without a visible reload.
      void queryClient.invalidateQueries({
        queryKey: trpc.gridData.get.queryKey()
      });
    } catch {
      if (previous) queryClient.setQueryData<GridDataOutput>(manualQueryKey, previous);
      setManualSaveError("A manual data change could not be saved and was reverted.");
    }
  }, [currentOrg.id, manualQueryKey, patchManualCell, queryClient]);
  const handleManualDelete = useCallback(async (metricId: string, day: string) => {
    setManualSaveError(null);
    const previous = patchManualCell(metricId, day, null);
    try {
      await trpcClient.manualData.deleteValue.mutate({
        orgId: currentOrg.id,
        metricId: Number(metricId),
        day
      });
      void queryClient.invalidateQueries({
        queryKey: trpc.gridData.get.queryKey()
      });
    } catch {
      if (previous) queryClient.setQueryData<GridDataOutput>(manualQueryKey, previous);
      setManualSaveError("A manual data change could not be saved and was reverted.");
    }
  }, [currentOrg.id, manualQueryKey, patchManualCell, queryClient]);
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const node = gridRef.current;
      if (!node) throw new Error("The current report is not ready to export.");
      const csv = captureAnalyticsCsv(node);
      const blob = new Blob([csv], {
        type: "text/csv"
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${currentOrg.slug}-media-performance-${view}-${data.dateRange.start}-to-${data.dateRange.end}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  }, [currentOrg.slug, data, view]);
  const performCurrentViewRefresh = useCallback(async () => {
    setConfirmingWideRefresh(false);
    try {
      const result = await refreshCurrentViewMutation.mutateAsync({
        orgSlug: params.orgSlug,
        start: activeGridInput.start,
        end: activeGridInput.end,
        platform: activeGridInput.platform ?? "all",
        comparisonMode: activeGridInput.comparisonMode
      });
      // Force one status read immediately. Production has already queued the
      // durable jobs at this point; observing `active > 0` starts the existing
      // bounded poll, and the next successful provider write invalidates grid.
      await queryClient.invalidateQueries({
        queryKey: trpc.gridData.reportingSyncStatus.pathKey()
      });
      const refreshStillActive = queryClient.getQueriesData<{
        active: number;
      }>({
        queryKey: trpc.gridData.reportingSyncStatus.pathKey()
      }).some(([, status]) => (status?.active ?? 0) > 0);
      setManualRefreshRunActive(result.queued > 0 && refreshStillActive);
      if (result.queued > 0) {
        showSuccess(`Refreshing ${result.days} completed day${result.days === 1 ? "" : "s"} from the provider APIs`);
      } else {
        showSuccess("No new refresh jobs were needed for this view");
      }
    } catch (error) {
      showError(error instanceof Error ? error.message : "Could not refresh the current view");
    }
  }, [activeGridInput, params.orgSlug, queryClient, refreshCurrentViewMutation]);
  const requestCurrentViewRefresh = useCallback(() => {
    if (manualRefreshPlan.days === 0) {
      showError("This view does not contain any completed reporting days");
      return;
    }
    if (manualRefreshPlan.days > MANUAL_VIEW_REFRESH_MAX_DAYS) {
      showError(`Choose a view with ${MANUAL_VIEW_REFRESH_MAX_DAYS} completed dates or fewer before refreshing`);
      return;
    }
    if (manualRefreshPlan.requiresConfirmation) {
      setConfirmingWideRefresh(true);
      return;
    }
    void performCurrentViewRefresh();
  }, [manualRefreshPlan, performCurrentViewRefresh]);
  const refreshInProgress = refreshCurrentViewMutation.isPending || manualRefreshRunActive && reportingJobsActive;
  if (!data.hasAccounts) {
    return <Page>
        <PageHeader title="Media Performance" actions={<WorkspaceDefaultActions />} />
        <PageBody>
          <EmptyState title="No Accounts Configured">
            Connect an ad account in Settings to see this Company's performance here.
          </EmptyState>
        </PageBody>
      </Page>;
  }
  return <Page>
      <PageHeader title="Media Performance" actions={<>
            <WorkspaceDefaultActions onCopyImage={() => void handleCopyPng()} onDownloadImage={() => void handleDownloadPng()} imageBusy={isExportingPng} onDownloadCsv={() => void handleExport()} onRefresh={requestCurrentViewRefresh} downloading={isExporting} refreshing={refreshInProgress} canRefresh={currentOrg.role !== "viewer"} />
          </>} tabs={<AnalyticsTabs view={view} onChange={next => navigateDashboard(next)} />} />
      <UpdatingChip active={updating || (reportingSyncStatus?.active ?? 0) > 0} />
      <PageBody>
        <div ref={gridRef} aria-busy={updating || (reportingSyncStatus?.active ?? 0) > 0 || undefined} className="x78zum5 xdt5ytf x5vihii xeuugli x1h1f4i9">
          <AnalyticsExportHeader organizationName={currentOrg.name} logoUrl={currentOrg.logo} dateRange={data.dateRange} segmentName={selectedSegment?.name} breakdownNames={data.activeBreakdowns?.map(item => item.name)} sectionName={ANALYTICS_TABS.find(tab => tab.value === view)?.label} reportContext={[platform === "all" ? "All channels" : platform, view === "overview" && changeDisplay !== "off" && exportComparison ? `Change: ${changeDisplay} vs ${exportComparison.start} – ${exportComparison.end}` : null, search.campaign ? `Campaign: ${selectedCampaign?.name ?? "Selected campaign"}` : null].filter(Boolean).join(" · ")} />
          {view !== "overview" && data.entityRowsNotice ? <p data-export-note className="x1ghz6dp x1e3avs9 x7x6yu3 x1evy7pa">
              {data.entityRowsNotice}
            </p> : null}
          <div data-export-controls>
            <ReportingToolbar dateRange={data.dateRange} preset={preset} grouping={grouping} platform={platform} availablePlatforms={data.availablePlatforms} freshness={data.platformHealth} showGrouping={view === "overview"} showPlatform showChange={view === "overview"} segment={search.segment} segments={segments} breakdown={search.breakdown} breakdowns={data.availableBreakdowns ?? []} assignments={view === "campaign" || view === "creative" ? {
            breakdowns: data.activeBreakdowns,
            scope: `${params.orgSlug}:${assignmentView}`,
            filters: assignmentFilters
          } : undefined} filterControls={view === "creative" && !selectedCreative && search.campaign ? <Chip label="Campaign" value={selectedCampaign?.name ?? "Selected campaign"} onClear={() => updateSearch({
            campaign: undefined
          })} clearLabel="Clear campaign filter" onClick={() => navigateDashboard("campaign")} /> : null} trailingControls={view === "creative" ? <CreativeLayoutControl value={creativeLayout} onChange={setCreativeLayout} /> : null} changeDisplay={changeDisplay} onDateApply={handleDateApply} onFilterApply={handleFilterApply} />
          </div>
          {view === "overview" && <>
              <DataGrid data={data} deltaDisplay={deltaDisplay} manualEditable={canEditManualData} onManualSave={handleManualSave} onManualDelete={handleManualDelete} />
              <div aria-live="assertive" role="status">
                {manualSaveError ? <p className="x1ghz6dp xnclacp x7x6yu3">{manualSaveError}</p> : null}
              </div>
            </>}
          {view === "campaign" && <EntityBreakdownWithExtras extraScope={extraScope} rows={data.campaignBreakdown} breakdowns={data.activeBreakdowns} kind="campaign" currency={data.currency} orgSlug={params.orgSlug} onNameClick={navigateCampaignCreatives} assignmentFilters={assignmentFilters} onAssignmentFiltersChange={setAssignmentFilters} />}
          {view === "creative" && selectedCreative && <div data-export-content>
              <CreativeDetail row={selectedCreative} currency={data.currency} status={creativeDetailStatus} statusLoading={creativeStatusLoading} statusSupported={selectedCreative.platform !== "google"} editHref={`/${params.orgSlug}/launch/edit`} canEdit={currentOrg.role !== "viewer"} onStatusAction={statusEntity ? handleCreativeStatusAction : undefined} statusActionPending={statusActionPending} onBack={() => updateSearch({
            creative: undefined
          })} />
            </div>}
          {view === "creative" && !selectedCreative && <>
              <EntityBreakdownWithExtras extraScope={extraScope} rows={creativeRows} breakdowns={data.activeBreakdowns} kind="creative" currency={data.currency} orgSlug={params.orgSlug} layout={creativeLayout} onSelect={navigateCreativeDetail} assignmentFilters={assignmentFilters} onAssignmentFiltersChange={setAssignmentFilters} />
            </>}
        </div>
      </PageBody>
      <ConfirmDialog open={confirmingWideRefresh} title={`Refresh ${manualRefreshPlan.days} completed days?`} message="This will request the selected dates, including the visible comparison period, directly from each selected provider and save the fresh results. Large views can use more provider quota and take a few minutes." confirmLabel="Refresh current view" onConfirm={() => void performCurrentViewRefresh()} onCancel={() => setConfirmingWideRefresh(false)} />
    </Page>;
}
const ANALYTICS_TABS: {
  value: AnalyticsView;
  label: string;
}[] = [{
  value: "overview",
  label: "Overview"
}, {
  value: "campaign",
  label: "By campaign"
}, {
  value: "creative",
  label: "By creative"
}];
function AnalyticsTabs({
  view,
  onChange
}: {
  view: AnalyticsView;
  onChange: (view: AnalyticsView) => void;
}) {
  // While a section navigation is still pending (its chunk or loader in
  // flight), highlight the tab the user clicked rather than the one whose data
  // is currently shown — the control reflects the choice instantly.
  const pendingView = useRouterState({
    select: state => state.status === "pending" ? viewFromPathname(state.location.pathname) : null
  });
  const active = pendingView ?? view;
  return <TabList aria-label="Media Performance views">
      {ANALYTICS_TABS.map(tab => <button key={tab.value} type="button" role="tab" aria-selected={active === tab.value} onClick={() => onChange(tab.value)} {...tabProps(active === tab.value)}>
          {tab.label}
        </button>)}
    </TabList>;
}
function renderableOrNull(url: string | null | undefined) {
  return isRenderablePreviewUrl(url) ? url : null;
}